import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import ChevronRightIcon from './ChevronRightIcon';
import './ContextMenu.css';

export interface ContextMenuSubItem {
  label: string;
  value: string;
}

export interface ContextMenuItem {
  label: string;
  action: string;
  danger?: boolean;
  divider?: boolean;
  disabled?: boolean;
  checked?: boolean;
  submenu?: ContextMenuSubItem[];
}

interface ContextMenuProps {
  items: ContextMenuItem[];
  position: { x: number; y: number };
  onItemClick: (action: string, submenuValue?: string) => void;
  onClose: () => void;
}

const VIEWPORT_MARGIN = 8;

function clampToViewport(el: HTMLElement, pos: { x: number; y: number }): { x: number; y: number } {
  const rect = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let { x, y } = pos;
  if (x + rect.width > vw - VIEWPORT_MARGIN) x = vw - rect.width - VIEWPORT_MARGIN;
  if (y + rect.height > vh - VIEWPORT_MARGIN) y = vh - rect.height - VIEWPORT_MARGIN;
  if (x < VIEWPORT_MARGIN) x = VIEWPORT_MARGIN;
  if (y < VIEWPORT_MARGIN) y = VIEWPORT_MARGIN;
  return { x, y };
}

const ContextMenu: React.FC<ContextMenuProps> = ({ items, position, onItemClick, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  useLayoutEffect(() => {
    if (menuRef.current)
      setAdjustedPosition(clampToViewport(menuRef.current, position));
  }, [position]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleItemClick = (item: ContextMenuItem, e: React.MouseEvent) => {
    if (item.disabled) return;
    if (item.submenu) {
      // Toggle submenu
      if (openSubmenu === item.action) {
        setOpenSubmenu(null);
      } else {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setSubmenuPosition({ x: rect.right, y: rect.top });
        setOpenSubmenu(item.action);
      }
    } else {
      onItemClick(item.action);
    }
  };

  const handleSubmenuItemClick = (action: string, value: string) => {
    onItemClick(action, value);
  };

  return (
    <div
      ref={menuRef}
      className="contextMenuPopup"
      style={{ left: adjustedPosition.x, top: adjustedPosition.y }}
    >
      {items.map((item, idx) => (
        <React.Fragment key={idx}>
          {item.divider && <div className="contextMenuDivider" />}
          <div
            className={`contextMenuItem ${item.danger ? 'danger' : ''} ${item.submenu ? 'hasSubmenu' : ''} ${openSubmenu === item.action ? 'submenuOpen' : ''} ${item.disabled ? 'disabled' : ''} ${item.checked !== undefined ? 'hasCheck' : ''}`}
            onClick={(e) => handleItemClick(item, e)}
          >
            {item.checked !== undefined && (
              <span className="contextMenuItemCheck">{item.checked ? '✓' : ''}</span>
            )}
            <span className="contextMenuItemLabel">{item.label}</span>
            {item.submenu && (
              <span className="contextMenuItemChevron">
                <ChevronRightIcon size={14} />
              </span>
            )}
          </div>
          {item.submenu && openSubmenu === item.action && (
            <div
              className="contextMenuSubmenu"
              style={{ left: submenuPosition.x, top: submenuPosition.y }}
              ref={el => { if (el) { const clamped = clampToViewport(el, submenuPosition); el.style.left = clamped.x + 'px'; el.style.top = clamped.y + 'px'; } }}
            >
              {item.submenu.length === 0 ? (
                <div className="contextMenuItemEmpty">No folders</div>
              ) : (
                item.submenu.map((subItem, subIdx) => (
                  <div
                    key={subIdx}
                    className="contextMenuItem"
                    onClick={() => handleSubmenuItemClick(item.action, subItem.value)}
                  >
                    {subItem.label}
                  </div>
                ))
              )}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default ContextMenu;
